import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Download, Lock, FileText, Image, File, FileSpreadsheet, Film, Music, Archive, Shield, AlertCircle, User } from 'lucide-react';
import { useParams } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILE_ICONS = { image: Image, pdf: FileText, document: FileText, spreadsheet: FileSpreadsheet, video: Film, audio: Music, archive: Archive, other: File };
const FILE_COLORS = { image: 'bg-pink-100 text-pink-600', pdf: 'bg-red-100 text-red-600', document: 'bg-blue-100 text-blue-600', spreadsheet: 'bg-emerald-100 text-emerald-600', video: 'bg-purple-100 text-purple-600', audio: 'bg-amber-100 text-amber-600', archive: 'bg-slate-100 text-slate-600', other: 'bg-slate-100 text-slate-600' };
const formatSize = (b) => { if (!b) return '0 B'; if (b < 1024) return `${b} B`; if (b < 1024*1024) return `${(b/1024).toFixed(1)} KB`; return `${(b/1024/1024).toFixed(1)} MB`; };

export const SharedFilePage = () => {
  const { shareToken } = useParams();
  const [fileInfo, setFileInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [verified, setVerified] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    loadFileInfo();
  }, [shareToken]);

  const loadFileInfo = async () => {
    try {
      const res = await axios.get(`${API}/public/share/${shareToken}`);
      setFileInfo(res.data);
      if (!res.data.has_password) setVerified(true);
    } catch (e) {
      setError(e.response?.status === 410 ? 'This share link has expired' : e.response?.status === 404 ? 'Share link not found' : 'Failed to load file information');
    } finally { setLoading(false); }
  };

  const handleVerify = async () => {
    if (!password) { toast.error('Enter password'); return; }
    try {
      await axios.post(`${API}/public/share/${shareToken}/verify`, { password });
      setVerified(true);
      toast.success('Password verified');
    } catch { toast.error('Incorrect password'); }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const url = `${API}/public/share/${shareToken}/download${fileInfo?.has_password ? `?password=${encodeURIComponent(password)}` : ''}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileInfo?.file_name || 'download';
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success('Download started');
    } catch (e) { toast.error(e.response?.data?.detail || 'Download failed'); }
    finally { setDownloading(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Card className="w-full max-w-md"><CardContent className="p-8 text-center">
        <AlertCircle className="h-12 w-12 mx-auto text-red-400 mb-4" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Cannot Access File</h2>
        <p className="text-muted-foreground">{error}</p>
      </CardContent></Card>
    </div>
  );

  const IconComp = FILE_ICONS[fileInfo?.file_type] || File;
  const colorClass = FILE_COLORS[fileInfo?.file_type] || FILE_COLORS.other;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="shared-file-page">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-3"><div className="h-16 w-16 rounded-2xl bg-black flex items-center justify-center"><span className="text-[#D4E100] font-bold text-lg">IK</span></div></div>
          <CardTitle className="text-lg">Shared File</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
            <div className={`h-12 w-12 rounded-lg ${colorClass} flex items-center justify-center shrink-0`}><IconComp className="h-6 w-6" /></div>
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate">{fileInfo?.file_name}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                <span>{formatSize(fileInfo?.file_size)}</span>
                <span>{fileInfo?.file_type}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <User className="h-3 w-3" /> Shared by <span className="font-medium text-foreground">{fileInfo?.shared_by}</span>
          </div>

          {fileInfo?.has_password && !verified && (
            <div className="space-y-3 border rounded-lg p-4">
              <div className="flex items-center gap-2 text-sm"><Lock className="h-4 w-4 text-amber-500" /><span className="font-medium">Password Protected</span></div>
              <Input type="password" placeholder="Enter password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleVerify()} data-testid="share-access-password" />
              <Button onClick={handleVerify} className="w-full" data-testid="share-verify-btn">Verify Password</Button>
            </div>
          )}

          {verified && (
            <Button onClick={handleDownload} disabled={downloading} className="w-full h-12 text-base" data-testid="share-download-btn">
              <Download className="h-5 w-5 mr-2" /> {downloading ? 'Downloading...' : 'Download File'}
            </Button>
          )}

          <p className="text-[10px] text-center text-muted-foreground mt-4">Powered by IKWorx Enterprise Management System</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SharedFilePage;
